import { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import type { HtmlBlock } from '@blogforge/shared';

interface HtmlBlockRendererProps {
  block: HtmlBlock;
}

export function HtmlBlockRenderer({ block }: HtmlBlockRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Sanitize HTML content
  const sanitizedContent = DOMPurify.sanitize(block.content, {
    ADD_ATTR: ['style', 'class'],
    ADD_TAGS: ['style'],
  });

  // Apply custom CSS
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const cssStr = block.attrs?.cssStr;

    // Remove any previously injected style tags from this block
    const existingStyle = container.querySelector(`[data-block-id="${block.id}"]`);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Inject new CSS if present
    if (cssStr) {
      const styleTag = document.createElement('style');
      styleTag.setAttribute('data-block-id', block.id);
      styleTag.textContent = cssStr;
      container.appendChild(styleTag);
    }

    // Cleanup
    return () => {
      const styleToRemove = container.querySelector(`[data-block-id="${block.id}"]`);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [block.id, block.attrs?.cssStr]);

  return (
    <div ref={containerRef} className="html-block-renderer" style={block.style}>
      {/* Render HTML content */}
      <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} className="html-block-content" />
    </div>
  );
}
